// Operator movement (riding/pushing and walking), collision, cutting, motor
// load, stall and fuel.

import type { Input, MowerState, EquipmentModel, OwnedEquipment, Layout } from './types';
import { ZONE_IDS } from './types';
import type { Ctx } from './ctx';
import { T_BED, T_GRASS, isSolidType, deckStepFor, cellAt, type SimGrid } from './layout';
import { deckCellsFor, mowerScale } from './contract';
import { damageFor, scattersOnPass } from './props';
import { setLength, wrapAngle, quantiseHeading, axisGap, CROSS_Q } from './cells';
import { hash01 } from './rng';
import { CATCHER_PER_PASS, dischargeSideOf } from './chute';
import {
  BUMP_SPEED_MULT, NEGLECTED_SPEED_MULT, SCATTER_CELLS, ZT_PIVOT_ANGLE, ZT_REAR_OFFSET, ZT_REAR_R, slopeDrift,
} from './vibe';
import { stampTracks } from './tracks';

/** Grass multipliers by GRASS_TYPES index: couch, buffalo, kikuyu, weedy. */
export const GRASS_MULT = [0.8, 1.0, 1.35, 0.9] as const;
export const WET_MULT = 1.5;
/**
 * Load normaliser (mm): chosen so a Redgum 46 (1 deck cell, tolerance 1.0)
 * at full throttle into uncut kikuyu (110 mm) at a 40 mm deck reads 1.15:
 * 1 × 1 × (110 − 40) × 1.35 / N = 1.15  →  N = 70 × 1.35 / 1.15 ≈ 82.17.
 */
export const LOAD_NORMALISER_MM = (70 * 1.35) / 1.15;
export const LOAD_AMBER = 0.85;
export const LOAD_RED = 1.0;
export const LOAD_TAU_S = 0.3;
export const HIGH_LOAD_GRACE_S = 2;
export const STALL_AFTER_S = 4;
export const RESTART_S = 1;
/**
 * The governor bogs instead of dying: with the blades on, the mower can only
 * hold the ground speed that keeps the grass just ahead at BOG_TARGET load
 * (just under the red line), down to BOG_MIN of top speed. So a mower shoved
 * into long grass slows itself rather than stalling, unless the grass is so
 * heavy (over BOG_TARGET / BOG_MIN at full speed: long wet kikuyu with the
 * deck down) that even a slow walk is too much. Load is normalised by the
 * mower's tolerance, so a stronger mower bogs less and stalls later.
 *
 * Feel pass (owner, 2026-09-27: "frustratingly slow, particularly in the tall
 * grass"): the floor went 0.28 → 0.4 (a slowdown, not a crawl), and the load
 * softens past LOAD_KNEE_MM of excess (see loadExcess), so it takes long wet
 * kikuyu to stall even at the higher floor.
 */
export const BOG_TARGET = 0.95;
export const BOG_MIN = 0.4;
/**
 * Excess grass (mm over the deck) past this loads the motor at LOAD_KNEE_SLOPE
 * per mm, not 1: long grass folds over under the deck rather than all meeting
 * the blade. At the knee, and under it, the load is exactly what it was, so
 * every lawn at its normal length (the normaliser's 110 mm kikuyu at a 40 mm
 * deck is 70 mm) loads as before; only neglected and paddock grass eases.
 */
export const LOAD_KNEE_MM = 70;
export const LOAD_KNEE_SLOPE = 0.5;

/** Excess (mm over the deck) → the mm that load counts. See LOAD_KNEE_MM. */
export function loadExcess(excessMm: number): number {
  if (!(excessMm > 0)) return 0;
  return excessMm <= LOAD_KNEE_MM ? excessMm : LOAD_KNEE_MM + (excessMm - LOAD_KNEE_MM) * LOAD_KNEE_SLOPE;
}

/** Fraction of top speed the governor allows when the grass ahead would load the motor `fullLoad` at full speed. */
export function bogFactor(fullLoad: number): number {
  return fullLoad <= BOG_TARGET ? 1 : Math.max(BOG_MIN, BOG_TARGET / fullLoad);
}
export const TURN_RATE_PUSH = 3.5;
export const TURN_RATE_RIDE = 2.2;
/**
 * Feel pass (owner, 2026-09-27: "we should be able to go a bit faster
 * generally"): every mower drives this much faster than its listed speedKmh.
 * The listing stays the machine's rating (the shop shows the pace it really
 * drives at, ShedKit statOf); par is still worked from the listing, so the
 * extra pace is the player's margin on par, not a new expectation.
 */
export const DRIVE_PACE = 1.15;
/** Walking with a hand tool (m/s, before √gameScale and heat). Was 1.4. */
export const WALK_SPEED = 1.55;
export const HIT_SPEED = 0.3;
export const MULCH_BLADE_LOSS = 0.08;
const CONTACT_MARGIN = 0.05;
const BLADE_WEAR_PER_CELL = 0.00001;

export function createMowerState(model: EquipmentModel, owned: OwnedEquipment, layout: Layout, grid: SimGrid): MowerState {
  const gs = layout.gameScale ?? 1;
  const deckStepMm = deckStepFor(layout);
  // Start at the TOP of the range (the spawn cell's zone range, else the
  // layout's): the gentlest cut, so a first-timer on kikuyu doesn't stall.
  const si = cellAt(grid, layout.spawn.x, layout.spawn.y);
  const top = si >= 0 && grid.type[si] === T_GRASS && !grid.dnm[si] && grid.hi[si] > 0 ? grid.hi[si] : layout.rangeMm[1];
  const minDeck = minDeckMm(deckStepMm);
  const deckMm = Math.min(100, Math.max(minDeck, Math.floor(top / deckStepMm + 1e-9) * deckStepMm));
  return {
    modelKey: model.key,
    deckCells: deckCellsFor(model, grid.cellM, gs),
    deckM: ((model.deckCm ?? 46) / 100) * mowerScale(model, gs),
    deckMm,
    deckStepMm,
    bladesOn: false,
    cruise: false,
    stalled: owned.fuelL <= 0 && model.power === 'petrol',
    load: 0,
    highLoadS: 0,
    redLoadS: 0,
    engineCondition: owned.engineCondition,
    bladeCondition: owned.bladeCondition,
    fuelL: owned.fuelL,
    tankL: model.tankL ?? 0,
    power: model.power,
    parkedX: layout.spawn.x,
    parkedY: layout.spawn.y,
    parkedHeading: layout.spawn.heading,
    dischargeSide: dischargeSideOf(model),
  };
}

/** Deck floor: 10 mm normally; fine-step (bowls/wicket) layouts go down to 2 mm. */
export function minDeckMm(step: number): number {
  return step < 10 ? 2 : 10;
}

/**
 * Engine rules for one tick at the mower's current (smoothed) load:
 * > 0.85 for more than 2 s → engine condition −1 %/s; ≥ 1.0 for 4 s → stall.
 */
export function applyLoadRules(m: MowerState, dt: number): 'stall' | null {
  if (m.load > LOAD_AMBER) {
    const before = m.highLoadS;
    m.highLoadS += dt;
    const over = m.highLoadS - Math.max(HIGH_LOAD_GRACE_S, before);
    if (over > 0) m.engineCondition = Math.max(0, m.engineCondition - 0.01 * over);
  } else {
    m.highLoadS = 0;
  }
  if (m.load >= LOAD_RED) {
    m.redLoadS += dt;
    if (m.redLoadS >= STALL_AFTER_S - 1e-9) return 'stall';
  } else {
    m.redLoadS = 0;
  }
  return null;
}

// ─── Collision ──────────────────────────────────────────────────────────

let sx = 0;
let sy = 0;
// Contact normal (unit, obstacle → operator) from the last propCheck that touched.
let nX = 0;
let nY = 0;
// Per-prop contact normal at first penetration this pass (scratch, grown on demand).
let cnx = new Float32Array(0);
let cny = new Float32Array(0);

/**
 * Push-out skin. Resolving to EXACTLY the touching distance left the operator
 * a float-rounding hair inside the solid on the next frame, which re-counted
 * as a hit, zeroed speed, and pinned the mower to any fence it touched (it
 * could only back straight out). Push out slightly past contact, and ignore
 * overlaps shallower than the skin.
 */
const SKIN = 1e-3;

/**
 * Speed after a collision. Sliding along a fence keeps full speed (the
 * push-out already removed the into-the-wall part of the move); only a
 * near-head-on block, where the operator barely moved, drops speed to what
 * was actually achieved. Re-deriving speed from the projected progress every
 * frame decayed it towards zero on any angled contact.
 */
export function slideSpeed(op: { x: number; y: number }, px: number, py: number, dt: number, before: number): number {
  if (dt <= 0) return 0;
  const moved = Math.hypot(op.x - px, op.y - py) / dt;
  return moved < before * 0.25 ? moved : before;
}

/** slideSpeed for a signed speed (reversing in drive mode keeps its sign). */
function signedSlide(op: { x: number; y: number }, px: number, py: number, dt: number, before: number): number {
  return before < 0 ? -slideSpeed(op, px, py, dt, -before) : slideSpeed(op, px, py, dt, before);
}

/** 0 = clear, 1 = within contact margin, 2 = penetrating (pushed to sx, sy). */
function propCheck(ctx: Ctx, pi: number, x: number, y: number, R: number): number {
  const p = ctx.layout.props[pi];
  if (p.r !== undefined) {
    const dx = x - p.x;
    const dy = y - p.y;
    const d = Math.hypot(dx, dy);
    const min = R + p.r;
    if (d >= min + CONTACT_MARGIN) return 0;
    nX = d < 1e-6 ? 1 : dx / d;
    nY = d < 1e-6 ? 0 : dy / d;
    if (d >= min - SKIN) return 1;
    const out = min + SKIN;
    if (d < 1e-6) {
      sx = p.x + out;
      sy = y;
    } else {
      sx = p.x + (dx / d) * out;
      sy = p.y + (dy / d) * out;
    }
    return 2;
  }
  const rot = p.rot ?? 0;
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  const dx = x - p.x;
  const dy = y - p.y;
  const lx = dx * c + dy * s;
  const ly = -dx * s + dy * c;
  const hw = (p.w ?? 0.5) / 2;
  const hh = (p.h ?? 0.5) / 2;
  const qx = lx < -hw ? -hw : lx > hw ? hw : lx;
  const qy = ly < -hh ? -hh : ly > hh ? hh : ly;
  const ox = lx - qx;
  const oy = ly - qy;
  const d = Math.hypot(ox, oy);
  if (d >= R + CONTACT_MARGIN) return 0;
  if (d > 1e-6) {
    nX = (ox * c - oy * s) / d;
    nY = (ox * s + oy * c) / d;
  }
  if (d >= R - SKIN) return 1;
  const RO = R + SKIN;
  let nlx: number;
  let nly: number;
  if (d > 1e-6) {
    nlx = qx + (ox / d) * RO;
    nly = qy + (oy / d) * RO;
  } else if (hw - Math.abs(lx) < hh - Math.abs(ly)) {
    nlx = (lx < 0 ? -1 : 1) * (hw + RO);
    nly = ly;
  } else {
    nlx = lx;
    nly = (ly < 0 ? -1 : 1) * (hh + RO);
  }
  sx = p.x + nlx * c - nly * s;
  sy = p.y + nlx * s + nly * c;
  if (d <= 1e-6) {
    const px = sx - x, py = sy - y, pl = Math.hypot(px, py) || 1;
    nX = px / pl;
    nY = py / pl;
  }
  return 2;
}

function solidAt(g: SimGrid, cx: number, cy: number): boolean {
  if (cx < 0 || cy < 0 || cx >= g.w || cy >= g.h) return true;
  return isSolidType(g.type[cy * g.w + cx]);
}

/**
 * Push the operator circle (radius R) out of solid props and solid cells.
 * Fills ctx.propTouch per prop: 1 = within the contact margin (brushing),
 * 2 = penetrated (actually blocked), with the contact normal at first
 * penetration kept for updateContacts. Returns true if anything was
 * penetrated (the caller stops the operator).
 *
 * Feature P2: `offX/offY` move the circle off the operator (the chute
 * circle); push-outs still move the operator. It then fills `touch` (the
 * chute's scratch) and leaves the body's contact normals alone. With no
 * offset and the default `touch` this is exactly the body pass it always was.
 */
export function resolveCollisions(ctx: Ctx, R: number, prevX: number, prevY: number, offX = 0, offY = 0, touch: Uint8Array = ctx.propTouch): boolean {
  const op = ctx.op;
  const g = ctx.grid;
  const props = ctx.layout.props;
  const ext = ctx.propExt;
  const body = touch === ctx.propTouch;
  touch.fill(0);
  if (cnx.length < props.length) {
    cnx = new Float32Array(props.length);
    cny = new Float32Array(props.length);
  }
  let hit = false;
  const cm = g.cellM;
  for (let pass = 0; pass < 2; pass++) {
    for (let pi = 0; pi < props.length; pi++) {
      const ex = ext[pi * 2];
      if (ex === 0) continue;
      const p = props[pi];
      const reach = R + CONTACT_MARGIN;
      const ox = op.x + offX, oy = op.y + offY;
      if (Math.abs(ox - p.x) > ex + reach || Math.abs(oy - p.y) > ext[pi * 2 + 1] + reach) continue;
      const r = propCheck(ctx, pi, ox, oy, R);
      if (r === 0) continue;
      if (body && r === 2 && touch[pi] < 2) {
        cnx[pi] = nX;
        cny[pi] = nY;
      }
      if (r > touch[pi]) touch[pi] = r;
      if (r === 2) {
        op.x = sx - offX;
        op.y = sy - offY;
        hit = true;
      }
    }
    const reach = R + CONTACT_MARGIN;
    const cx0 = op.x + offX, cy0 = op.y + offY;
    const x0 = Math.floor((cx0 - reach) / cm);
    const x1 = Math.floor((cx0 + reach) / cm);
    const y0 = Math.floor((cy0 - reach) / cm);
    const y1 = Math.floor((cy0 + reach) / cm);
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        if (!solidAt(g, cx, cy)) continue;
        const ox = op.x + offX, oy = op.y + offY;
        const bx = cx * cm;
        const by = cy * cm;
        const qx = ox < bx ? bx : ox > bx + cm ? bx + cm : ox;
        const qy = oy < by ? by : oy > by + cm ? by + cm : oy;
        const dx = ox - qx;
        const dy = oy - qy;
        const d = Math.hypot(dx, dy);
        if (d >= reach) continue;
        const inGrid = cx >= 0 && cy >= 0 && cx < g.w && cy < g.h;
        const pa = inGrid ? g.propAt[cy * g.w + cx] : -1;
        if (pa >= 0 && !touch[pa]) touch[pa] = 1;
        if (d >= R - SKIN) continue;
        hit = true;
        if (pa >= 0 && touch[pa] < 2) {
          touch[pa] = 2;
          if (body) {
            cnx[pa] = d > 1e-6 ? dx / d : -Math.cos(op.heading);
            cny[pa] = d > 1e-6 ? dy / d : -Math.sin(op.heading);
          }
        }
        if (d > 1e-6) {
          op.x = qx + (dx / d) * (R + SKIN) - offX;
          op.y = qy + (dy / d) * (R + SKIN) - offY;
        } else {
          op.x = prevX;
          op.y = prevY;
        }
      }
    }
  }
  return hit;
}

/**
 * Debounced obstacle-hit bookkeeping after a collision pass. propContact per
 * prop: 0 clear, 1 touching (not charged), 2 charged for this contact.
 * Only the MOWER actually blocked by a penalty prop while closing on it
 * faster than HIT_SPEED costs anything: brushing past within the contact
 * margin, sliding along it, or any walker contact just blocks.
 */
function updateContacts(ctx: Ctx, onMower: boolean, speedBefore: number): void {
  const props = ctx.layout.props;
  const touch = ctx.propTouch;
  const contact = ctx.propContact;
  const hx = Math.cos(ctx.op.heading), hy = Math.sin(ctx.op.heading);
  for (let pi = 0; pi < props.length; pi++) {
    const t = touch[pi];
    if (!t) {
      contact[pi] = 0;
      continue;
    }
    if (contact[pi] === 2) continue;
    contact[pi] = 1;
    if (t !== 2 || !onMower) continue;
    const p = props[pi];
    const closing = -(hx * cnx[pi] + hy * cny[pi]) * speedBefore;
    // Feature P1: the prop catalogue decides the damage; `none` (soft edges,
    // `penalty: false`) blocks but never bills, exactly as `penalty: false` did.
    const damage = damageFor(p);
    if (damage !== 'none' && closing > HIT_SPEED) {
      contact[pi] = 2;
      ctx.obstacleHits++;
      ctx.hitLog.push({ kind: p.kind, damage });
      ctx.events.push({ type: 'obstacle-hit', propId: p.id, kind: p.kind, damage });
    }
  }
}

/**
 * Feature P2: debounced chute hits, after the chute's collision pass. Same
 * rules as the body (charged once per continuous contact, re-armed when it
 * clears, only on the mower, only a penetrated penalty prop), except that a
 * chute drags rather than rams: any travel faster than HIT_SPEED counts,
 * not just closing speed. A prop the body already charged for this contact
 * is not charged twice.
 */
function updateChuteContacts(ctx: Ctx, speedBefore: number): void {
  const props = ctx.layout.props;
  const touch = ctx.chuteTouch;
  const contact = ctx.chuteContact;
  for (let pi = 0; pi < props.length; pi++) {
    const t = touch[pi];
    if (!t) {
      contact[pi] = 0;
      continue;
    }
    if (contact[pi] === 2) continue;
    contact[pi] = 1;
    if (t !== 2) continue;
    if (ctx.propContact[pi] === 2) {
      contact[pi] = 2;
      continue;
    }
    const p = props[pi];
    const damage = damageFor(p);
    if (damage !== 'none' && Math.abs(speedBefore) > HIT_SPEED) {
      contact[pi] = 2;
      ctx.obstacleHits++;
      ctx.hitLog.push({ kind: p.kind, damage, via: 'chute' });
      ctx.events.push({ type: 'obstacle-hit', propId: p.id, kind: p.kind, damage, via: 'chute' });
    }
  }
}

// ─── Mower ──────────────────────────────────────────────────────────────

export function mowerTopSpeed(ctx: Ctx): number {
  return topSpeedMs(ctx.mowerModel, ctx.gs);
}

/** A mower's forwards top speed (m/s) on a layout: its listed km/h × DRIVE_PACE, × the mower's gameScale share. */
export function topSpeedMs(model: Pick<EquipmentModel, 'speedKmh' | 'rideOn'>, gameScale = 1): number {
  return ((model.speedKmh ?? 4) / 3.6) * DRIVE_PACE * mowerScale(model, gameScale);
}

function stall(ctx: Ctx): void {
  const m = ctx.mower;
  m.stalled = true;
  m.bladesOn = false;
  m.cruise = false;
  m.highLoadS = 0;
  m.redLoadS = 0;
  ctx.op.speed = 0;
  ctx.restartT = -1;
  ctx.events.push({ type: 'stall' });
}

// ─── Drive-relative steering (Input.drive) ──────────────────────────────
// Up = forwards, down = reverse, sideways = turn, whatever the camera does.

/**
 * Reverse top speed as a fraction of forwards. Feel pass (owner, 2026-09-27:
 * "a bit faster in reverse"): push 0.4 → 0.65, ride-on 0.5 → 0.7, walking
 * 0.5 → 0.6. With DRIVE_PACE that is 1.9× the old reverse on a push mower.
 */
export const REVERSE_PUSH = 0.65;
export const REVERSE_RIDE = 0.7;
export const REVERSE_WALK = 0.6;
/** Steer alone creeps a non-zero-turn machine along at this × |steer| × top (it can't spin on the spot). */
export const PIVOT_CREEP = 0.25;
/** Drive throttle inside ±this is no throttle (and not reversing). */
export const DRIVE_DEAD = 0.02;

/**
 * Is this drive throttle backing up? The one test both the sim (driveTurn's
 * car-style flip) and the stick (controls.ts driveStick, which pre-flips so
 * the turn crossfades instead of snapping) use, so they can't disagree.
 */
export function reversing(throttle: number): boolean {
  return throttle < -DRIVE_DEAD;
}

const clamp1 = (v: number) => (v > 1 ? 1 : v < -1 ? -1 : v || 0);

/**
 * Heading change for drive steering. Forwards, steer right turns right
 * (heading increases). Backing up it reverses, as in a car: steer right
 * swings the rear right, so the nose goes left. Steer alone (no throttle)
 * turns the way the stick points.
 */
function driveTurn(steer: number, throttle: number, rate: number, dt: number): number {
  return (reversing(throttle) ? -steer : steer) * rate * dt;
}

function steerDrive(ctx: Ctx, dt: number, d: { throttle: number; steer: number }, running: boolean, top: number, capMs: number): void {
  const m = ctx.mower;
  const op = ctx.op;
  const model = ctx.mowerModel;
  const steer = Math.abs(clamp1(d.steer)) > DRIVE_DEAD ? clamp1(d.steer) : 0;
  let thr = clamp1(d.throttle);
  if (thr > DRIVE_DEAD) {
    if (m.cruise) ctx.cruiseThrottle = thr;
  } else if (thr < -DRIVE_DEAD) {
    // Backing up is the brake: it drops cruise.
    m.cruise = false;
  } else {
    thr = m.cruise ? ctx.cruiseThrottle : 0;
  }
  ctx.throttle = running ? Math.abs(thr) : 0;
  if (running && steer) {
    op.heading = wrapAngle(op.heading + driveTurn(steer, thr, model.rideOn ? TURN_RATE_RIDE : TURN_RATE_PUSH, dt));
  }
  let target = thr >= 0 ? thr * top : thr * top * (model.rideOn ? REVERSE_RIDE : REVERSE_PUSH);
  if (model.style !== 'zero-turn' && steer) {
    const creep = Math.abs(steer) * PIVOT_CREEP * top;
    if (Math.abs(target) < creep) target = thr < 0 ? -creep : creep;
  }
  if (Math.abs(target) > capMs) target = Math.sign(target) * capMs;
  op.speed = running ? op.speed + (target - op.speed) * Math.min(1, dt * 4) : 0;
}

export function stepMower(ctx: Ctx, dt: number, input: Input): void {
  const m = ctx.mower;
  const op = ctx.op;
  const model = ctx.mowerModel;

  if (input.deckDelta) {
    const next = m.deckMm + Math.sign(input.deckDelta) * m.deckStepMm;
    m.deckMm = Math.min(100, Math.max(minDeckMm(m.deckStepMm), next));
  }
  const top = mowerTopSpeed(ctx);
  if (input.toggleCruise) {
    m.cruise = !m.cruise;
    if (m.cruise) ctx.cruiseThrottle = Math.max(ctx.throttle, top > 0 ? op.speed / top : 0);
  }
  if (input.restartEngine && m.stalled && !ctx.outOfFuel && ctx.restartT < 0) ctx.restartT = 0;
  if (ctx.restartT >= 0) {
    ctx.restartT += dt;
    if (ctx.restartT >= RESTART_S - 1e-9) {
      ctx.restartT = -1;
      m.stalled = false;
      m.highLoadS = 0;
      m.redLoadS = 0;
      ctx.events.push({ type: 'restart' });
    }
  }
  const running = !m.stalled;
  // Feature P9: a neglected yard's long grass slows the mower down.
  const baseTop = top;
  const topHere = ctx.neglected && inLongGrass(ctx) ? top * NEGLECTED_SPEED_MULT : top;
  // The governor bogs in heavy grass (blades on): ground speed is capped at what keeps the load under the red line.
  const capMs = running && input.toolHeld ? bogFactor(grassLoad(ctx)) * top : Infinity;
  const zt = ctx.zeroTurn && model.style === 'zero-turn';
  const h0 = op.heading;

  // Steering.
  if (input.drive) {
    steerDrive(ctx, dt, input.drive, running, topHere, capMs);
  } else {
    const mag = Math.min(1, Math.hypot(input.moveX, input.moveY));
    let targetH = op.heading;
    let throttle = 0;
    if (mag > 0.05) {
      targetH = Math.atan2(input.moveY, input.moveX);
      throttle = mag;
      if (m.cruise) ctx.cruiseThrottle = mag;
    } else if (m.cruise) {
      throttle = ctx.cruiseThrottle;
    }
    ctx.throttle = running ? throttle : 0;
    const diff = wrapAngle(targetH - op.heading);
    if (running && throttle > 0) {
      const maxTurn = (model.rideOn ? TURN_RATE_RIDE : TURN_RATE_PUSH) * dt;
      op.heading = wrapAngle(op.heading + (diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff));
    }
    // Feature P9: a zero-turn asked for a sharp turn pivots on the spot instead of creeping round.
    const turnFactor = zt && Math.abs(diff) > ZT_PIVOT_ANGLE ? 0 : Math.max(0.25, 0.5 + 0.5 * Math.cos(diff));
    const targetSpeed = running ? Math.min(throttle * topHere * turnFactor, capMs) : 0;
    op.speed = running ? op.speed + (targetSpeed - op.speed) * Math.min(1, dt * 4) : 0;
  }
  // Feature P9: pivoting, the zero-turn's back end swings wide (a circle behind).
  if (zt && op.heading !== h0 && Math.abs(op.speed) < 0.25 * baseTop) {
    if (rearSwingBlocked(ctx, h0)) op.heading = h0;
    else if (!ctx.ztPivoted) {
      ctx.ztPivoted = true;
      ctx.events.push({ type: 'zero-turn-pivot' });
    }
  }

  // Move + collide. (Speed is negative only when reversing in drive mode.)
  const px = op.x;
  const py = op.y;
  const speedBefore = op.speed;
  op.x += Math.cos(op.heading) * op.speed * dt;
  op.y += Math.sin(op.heading) * op.speed * dt;
  // Feature P9: on a slope the mower drifts downhill a little, more the faster it goes.
  if (ctx.slopeDrift && ctx.layout.slope && baseTop > 0) {
    const [dx, dy] = slopeDrift(ctx.layout.slope, Math.abs(op.speed) / baseTop, ctx.gs, dt);
    op.x += dx;
    op.y += dy;
  }
  if (resolveCollisions(ctx, ctx.mowerRadius, px, py)) op.speed = signedSlide(op, px, py, dt, speedBefore);
  updateContacts(ctx, true, speedBefore);
  // Feature P2: the chute's circle, beside the deck on its side. Its push-out
  // moves the mower too (you can't cut close on the chute side).
  if (ctx.chuteSign !== 0) {
    const k = ctx.chuteSign * ctx.chuteLat;
    const cx = op.x, cy = op.y;
    if (resolveCollisions(ctx, ctx.chuteR, cx, cy, -Math.sin(op.heading) * k, Math.cos(op.heading) * k, ctx.chuteTouch)) {
      op.speed = signedSlide(op, px, py, dt, op.speed);
    }
    updateChuteContacts(ctx, speedBefore);
  }
  // Feature P9: passable props are a bump (and loose edging scatters), tracks behind the wheels.
  if (ctx.passable && ctx.passIdx.length) rollOver(ctx);
  if (ctx.tyreTracks) stampTracks(ctx);
  m.parkedX = op.x;
  m.parkedY = op.y;
  m.parkedHeading = op.heading;

  // Blades, cutting, load.
  m.bladesOn = running && input.toolHeld;
  let raw = 0;
  if (m.bladesOn) {
    cutUnderDeck(ctx);
    raw = frontLoad(ctx, top);
  } else {
    ctx.inDnm = false;
  }
  m.load += (raw - m.load) * (1 - Math.exp(-dt / LOAD_TAU_S));
  if (m.load < 1e-6) m.load = 0;
  if (running && applyLoadRules(m, dt) === 'stall') stall(ctx);

  // Fuel.
  if (!m.stalled && m.power === 'petrol') {
    const burn = Math.min(m.fuelL, ((model.burnLph ?? 0) * Math.max(0.3, ctx.throttle) * dt) / 3600);
    m.fuelL -= burn;
    ctx.fuelUsedL += burn;
    if (m.fuelL <= 0) {
      m.fuelL = 0;
      ctx.outOfFuel = true;
      m.stalled = true;
      m.bladesOn = false;
      m.cruise = false;
      op.speed = 0;
      ctx.events.push({ type: 'out-of-fuel' });
    }
  }
}

/** Feature P9: the cell under the mower is grass still above its range (a neglected yard's long stuff). */
function inLongGrass(ctx: Ctx): boolean {
  const g = ctx.grid;
  const i = cellAt(g, ctx.op.x, ctx.op.y);
  return i >= 0 && g.type[i] === T_GRASS && g.hi[i] > 0 && g.lengthMm[i] > g.hi[i];
}

/**
 * Feature P9: would the zero-turn's rear circle, at the new heading, dig
 * into something it wasn't already against at `h0`? (Parked flush against a
 * fence it may still pivot away.) Leaves the operator where it was.
 */
function rearSwingBlocked(ctx: Ctx, h0: number): boolean {
  const op = ctx.op;
  const x = op.x, y = op.y, h = op.heading;
  const back = ctx.mowerRadius * ZT_REAR_OFFSET;
  const R = ctx.mowerRadius * ZT_REAR_R;
  const hitNew = resolveCollisions(ctx, R, x, y, -Math.cos(h) * back, -Math.sin(h) * back, ctx.rearTouch);
  op.x = x;
  op.y = y;
  if (!hitNew) return false;
  const hitOld = resolveCollisions(ctx, R, x, y, -Math.cos(h0) * back, -Math.sin(h0) * back, ctx.rearTouch);
  op.x = x;
  op.y = y;
  return !hitOld;
}

/**
 * Feature P9: rolling onto a passable prop (per contact) knocks the speed to
 * BUMP_SPEED_MULT and emits `bump`; loose edging also scatters onto up to
 * SCATTER_CELLS open cells around the mower (grid.scatter, visual only).
 */
function rollOver(ctx: Ctx): void {
  const op = ctx.op;
  const props = ctx.layout.props;
  const R = ctx.mowerRadius;
  for (const pi of ctx.passIdx) {
    const p = props[pi];
    const over = propCheck(ctx, pi, op.x, op.y, R) === 2;
    if (!over) {
      ctx.passOver[pi] = 0;
      continue;
    }
    if (ctx.passOver[pi]) continue;
    ctx.passOver[pi] = 1;
    op.speed *= BUMP_SPEED_MULT;
    const scatter = scattersOnPass(p);
    if (scatter) scatterAround(ctx, pi);
    ctx.events.push({ type: 'bump', propId: p.id, kind: p.kind, ...(scatter ? { scatter: true } : {}) });
  }
}

function scatterAround(ctx: Ctx, pi: number): void {
  const g = ctx.grid;
  const sc = g.scatter;
  if (!sc) return;
  const cx = Math.floor(ctx.op.x / g.cellM);
  const cy = Math.floor(ctx.op.y / g.cellM);
  let n = 0;
  for (let dy = -1; dy <= 1 && n < SCATTER_CELLS; dy++) {
    for (let dx = -1; dx <= 1 && n < SCATTER_CELLS; dx++) {
      const x = cx + dx, y = cy + dy;
      if (x < 0 || y < 0 || x >= g.w || y >= g.h) continue;
      const i = y * g.w + x;
      if (isSolidType(g.type[i]) || hash01(i * 31 + pi + ctx.tick) > 0.6) continue;
      n++;
      if (!sc[i]) {
        sc[i] = 1;
        g.dirty.push(i);
      }
    }
  }
}

/** Cut every cuttable grass cell whose centre lies under the deck rectangle. */
function cutUnderDeck(ctx: Ctx): void {
  const g = ctx.grid;
  const m = ctx.mower;
  const op = ctx.op;
  const cm = g.cellM;
  const hw = (m.deckCells * cm) / 2 + 1e-6;
  const hl = cm / 2 + 1e-6;
  const fx = Math.cos(op.heading);
  const fy = Math.sin(op.heading);
  const ex = Math.abs(fx) * hl + Math.abs(fy) * hw;
  const ey = Math.abs(fy) * hl + Math.abs(fx) * hw;
  const x0 = Math.max(0, Math.ceil((op.x - ex) / cm - 0.5));
  const x1 = Math.min(g.w - 1, Math.floor((op.x + ex) / cm - 0.5));
  const y0 = Math.max(0, Math.ceil((op.y - ey) / cm - 0.5));
  const y1 = Math.min(g.h - 1, Math.floor((op.y + ey) / cm - 0.5));
  // Grass lies the way the deck travels: reversing lays the opposite shade.
  const q = quantiseHeading(op.speed < 0 ? op.heading + Math.PI : op.heading);
  const ragged = m.bladeCondition < 0.5 ? ((0.5 - m.bladeCondition) / 0.5) * 6 : 0;
  const s2 = g.stripe2;
  let dnmZone = -1;
  let cutCount = 0;
  for (let cy = y0; cy <= y1; cy++) {
    const ccy = (cy + 0.5) * cm - op.y;
    for (let cx = x0; cx <= x1; cx++) {
      const ccx = (cx + 0.5) * cm - op.x;
      const along = ccx * fx + ccy * fy;
      const lat = -ccx * fy + ccy * fx;
      if (along > hl || along < -hl || lat > hw || lat < -hw) continue;
      const i = cy * g.w + cx;
      if (g.type[i] !== T_GRASS) continue;
      if (g.dnm[i]) {
        if (g.zoneFine[g.zone[i]]) dnmZone = g.zone[i];
        continue;
      }
      if (g.trim[i]) continue;
      const target = ragged > 0 ? m.deckMm + ragged * (0.5 + 0.5 * hash01(i)) : m.deckMm;
      let changed = false;
      if (g.lengthMm[i] > target) {
        setLength(ctx, i, target);
        changed = true;
        cutCount++;
      }
      if (!g.cut[i]) {
        g.cut[i] = 1;
        changed = true;
      }
      if (g.stripe[i] !== q) {
        // Crossed stripes: a pass across an earlier one (60° or more) keeps it underneath.
        if (s2 && ctx.striped[i] && axisGap(g.stripe[i], q) >= CROSS_Q) s2[i] = g.stripe[i] | 1;
        g.stripe[i] = q;
        changed = true;
      }
      if (s2) ctx.striped[i] = 1;
      if (changed) g.dirty.push(i);
    }
  }
  if (cutCount) {
    ctx.cellsCut += cutCount;
    m.bladeCondition = Math.max(0, m.bladeCondition - BLADE_WEAR_PER_CELL * cutCount);
  }
  if (dnmZone >= 0) {
    if (!ctx.inDnm) {
      ctx.violations++;
      ctx.violationZones.push(ZONE_IDS[dnmZone]);
      ctx.events.push({ type: 'violation', zone: ZONE_IDS[dnmZone] });
    }
    ctx.inDnm = true;
  } else {
    ctx.inDnm = false;
  }

  // Ground objects under the deck get mulched.
  const objs = ctx.objects;
  for (let k = 0; k < objs.length; k++) {
    const o = objs[k];
    if (o.state !== 'ground') continue;
    const dx = o.x - op.x;
    const dy = o.y - op.y;
    const along = dx * fx + dy * fy;
    const lat = -dx * fy + dy * fx;
    if (Math.abs(along) <= hl + 0.1 && Math.abs(lat) <= hw + 0.1) {
      o.state = 'mulched';
      m.bladeCondition = Math.max(0, m.bladeCondition - MULCH_BLADE_LOSS);
      ctx.events.push({ type: 'object-mulched', objectId: o.id });
    }
  }

  // Catcher: loose leaves under the deck go in the bag — Feature P2: at
  // CATCHER_PER_PASS per pass, not all at once. A pass starts when a loose
  // leaf comes under the deck (it wasn't the step before: `catcherUnder`
  // holds the last tick it was under the cutting deck); the bag takes a
  // running CATCHER_PER_PASS share of them, deterministically (no rng draw),
  // and the rest stay where they lie for the next pass: 60 → 84 → 94%.
  // Feature P6 (debris rules): that 60% is the collect; every leaf a pass
  // reaches counts cleared for a clear (`leafCaught`), and a bed's leaves are
  // the rake's job, so the catcher skips them.
  if (ctx.mowerModel.collector) {
    const L = ctx.leaves;
    const under = ctx.catcherUnder;
    const tick = ctx.tick;
    const g = ctx.grid;
    for (let k = 0; k < L.count; k++) {
      if (L.state[k] !== 0) continue;
      if (ctx.debris) {
        const ci = cellAt(g, L.x[k], L.y[k]);
        if (ci >= 0 && g.type[ci] === T_BED) continue;
      }
      const dx = L.x[k] - op.x;
      const dy = L.y[k] - op.y;
      if (Math.abs(dx) > ex + 0.2 || Math.abs(dy) > ey + 0.2) continue;
      const along = dx * fx + dy * fy;
      const lat = -dx * fy + dy * fx;
      if (Math.abs(along) > hl + 0.2 || Math.abs(lat) > hw) continue;
      const cont = under[k] !== 0 && under[k] === tick - 1;
      under[k] = tick;
      if (cont) continue;
      if (ctx.debris) ctx.leafCaught[k] = 1;
      ctx.catcherOwed += CATCHER_PER_PASS;
      if (ctx.catcherOwed >= 1 - 1e-9) {
        ctx.catcherOwed -= 1;
        L.state[k] = 2;
        L.vx[k] = 0;
        L.vy[k] = 0;
        ctx.collectorCount++;
      }
    }
  }
}

/**
 * Raw (unsmoothed) motor load from the grass just ahead of the deck:
 * speed_factor × deck_cells × mean(loadExcess(excess) × grassMult × wet) / N / tolerance,
 * where excess = max(0, length − deckMm) sampled at deck_cells points one
 * cell ahead (the grass about to be cut), and speed_factor = speed / top.
 */
function frontLoad(ctx: Ctx, top: number): number {
  const n = ctx.mower.deckCells;
  const speedFactor = top > 0 ? Math.min(1, Math.abs(ctx.op.speed) / top) : 0;
  return (speedFactor * n * meanExcessAhead(ctx)) / LOAD_NORMALISER_MM / (ctx.mowerModel.tolerance ?? 1);
}

/** The load the grass just ahead would put on the motor at full speed (frontLoad at speed_factor 1). */
function grassLoad(ctx: Ctx): number {
  return (ctx.mower.deckCells * meanExcessAhead(ctx)) / LOAD_NORMALISER_MM / (ctx.mowerModel.tolerance ?? 1);
}

/** mean(loadExcess(excess) × grassMult × wet) over the deck_cells sample points one cell ahead. */
function meanExcessAhead(ctx: Ctx): number {
  const g = ctx.grid;
  const m = ctx.mower;
  const op = ctx.op;
  const cm = g.cellM;
  const n = m.deckCells;
  // Reversing (drive mode) loads up on the grass behind the deck.
  const fx = op.speed < 0 ? -Math.cos(op.heading) : Math.cos(op.heading);
  const fy = op.speed < 0 ? -Math.sin(op.heading) : Math.sin(op.heading);
  let sum = 0;
  for (let k = 0; k < n; k++) {
    const off = (k - (n - 1) / 2) * cm;
    const i = cellAt(g, op.x + fx * cm - fy * off, op.y + fy * cm + fx * off);
    if (i < 0 || g.type[i] !== T_GRASS || g.trim[i] || g.dnm[i]) continue;
    const excess = g.lengthMm[i] - m.deckMm;
    if (excess <= 0) continue;
    sum += loadExcess(excess) * GRASS_MULT[g.grass[i]] * (g.wet[i] ? WET_MULT : 1);
  }
  return sum / n;
}

// ─── Walking ────────────────────────────────────────────────────────────

const SHADE_KINDS = new Set(['gum-tree', 'shade-sail', 'verandah', 'annexe', 'shed', 'canteen']);

export function inShade(ctx: Ctx): boolean {
  const op = ctx.op;
  for (const p of ctx.layout.props) {
    if (!SHADE_KINDS.has(p.kind)) continue;
    const r = p.r ?? Math.max(p.w ?? 0, p.h ?? 0) / 2;
    if (Math.hypot(op.x - p.x, op.y - p.y) <= r + 2) return true;
  }
  return false;
}

export function stepWalker(ctx: Ctx, dt: number, input: Input): void {
  const op = ctx.op;
  const mag = Math.min(1, Math.hypot(input.moveX, input.moveY));
  const top = WALK_SPEED * Math.sqrt(ctx.gs) * (1 - 0.3 * ctx.heat);
  if (input.drive) {
    const steer = clamp1(input.drive.steer);
    const thr = clamp1(input.drive.throttle);
    if (Math.abs(steer) > DRIVE_DEAD) op.heading = wrapAngle(op.heading + driveTurn(steer, thr, TURN_RATE_PUSH, dt));
    op.speed = thr > DRIVE_DEAD ? thr * top : reversing(thr) ? thr * top * REVERSE_WALK : 0;
  } else if (mag > 0.05) {
    const diff = wrapAngle(Math.atan2(input.moveY, input.moveX) - op.heading);
    const maxTurn = 8 * dt;
    op.heading = wrapAngle(op.heading + (diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff));
    op.speed = mag * top * Math.max(0.4, 0.5 + 0.5 * Math.cos(diff));
  } else {
    op.speed = 0;
  }
  const px = op.x;
  const py = op.y;
  op.x += Math.cos(op.heading) * op.speed * dt;
  op.y += Math.sin(op.heading) * op.speed * dt;
  // Walking into something just stops you — never a penalty.
  if (resolveCollisions(ctx, ctx.walkerRadius, px, py)) op.speed = signedSlide(op, px, py, dt, op.speed);
  updateContacts(ctx, false, 0);
}
